/**
 * CASE ROUTING CONTRACT (NON-NEGOTIABLE)
 * 
 * - CaseLayout MUST appear exactly once in the router tree
 * - All /cases/:caseId/* routes MUST be children of that layout
 * - No child route may wrap CaseLayout again
 * - Param names are enforced: caseId, draftId, filingId, evidenceId (NO aliases)
 * 
 * Violations will cause:
 * - Silent production failures (duplicate headers, broken navigation)
 * - State-based navigation masquerading as route-based navigation
 * - Components mounting without proper URL context
 * 
 * Runtime assertions in CaseLayout will detect violations in development.
 */

import type { ComponentType } from "react";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, useRouteError } from "react-router-dom";
import RequireAuth from "@/guards/RequireAuth";
import RequireProAccess from "@/guards/RequireProAccess";
import RequireCaseAccess from "@/guards/RequireCaseAccess";
import RequireSrlHasCase from "@/guards/RequireSrlHasCase";
import RequireSubscription from "@/features/billing/RequireSubscription";
import RequireActiveSubscription from "@/guards/RequireActiveSubscription";
import RequireVerification from "@/guards/RequireVerification";
import RequireAdminAccess from "@/guards/RequireAdminAccess";
import RequireSuperAdmin from "@/guards/RequireSuperAdmin";
import { PHASES } from "@/config/phases";
import LoginPage from "@/pages/auth/LoginPage";
import SignupPage from "@/pages/auth/SignupPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import EmailVerificationInterstitialPage from "@/pages/auth/EmailVerificationInterstitialPage";
import SubscribePage from "@/pages/SubscribePage";
import ActionCodeRoute from "@/routes/ActionCodeRoute";
import ProfilePage from "@/pages/profile/ProfilePage";
import AppShell from "@/components/AppShell.jsx";
import { ErrorBoundary } from "@/components/ErrorBoundary.tsx";
import RouteError from "@/components/RouteError";
import SettingsPage from "@/pages/SettingsPage";
import SearchPage from "@/pages/SearchPage";
import HelpPage from "@/pages/HelpPage";
import HelpCenterResearchPage from "@/pages/help/HelpCenterResearchPage"; // Help Center V3 - Research Hub
import ShareViewerPage from "@/routes/public/ShareViewerPage";
import InviteAcceptPage from "@/routes/public/InviteAcceptPage";
import AppLayout from "@/components/layout/AppLayout";
import { CaseLayout } from "@/components/layout/CaseLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import ProPortalLayout from "@/components/pro/ProPortalLayout";
import PublicStandaloneLayout from "@/components/layout/PublicStandaloneLayout";
import LoadingSpinner from "@/components/LoadingSpinner";
import NewCasePage from "@/pages/cases/NewCasePage";
import CasesPage from "@/pages/cases/CasesPage";
import CreateCasePage from "@/pages/onboarding/CreateCasePage";
import { OnboardingBasicPage } from "@/pages/onboarding/OnboardingBasicPage";
import { OnboardingConfirmPage } from "@/pages/onboarding/OnboardingConfirmPage";
import SetupWizardPage from "@/pages/onboarding/SetupWizardPage";
import ResolveCurrentCaseRoute from "@/components/routing/ResolveCurrentCaseRoute";
import { useParams, Navigate as RouterNavigate } from "react-router-dom";

// DEV-ONLY: Debug page for P0 routing contract
const CaseRoutingDebugPage = lazy(() => import("@/pages/debug/CaseRoutingDebugPage"));

// P0: Legacy route redirect
const LegacyEditorRedirect = lazy(() => import("@/components/routing/LegacyEditorRedirect"));
const BatesPage = lazy(() => import("@/routes/cases/exhibits/BatesPage"));

// ⚡ Code-split heavy pages to reduce initial bundle size
// These pages are loaded on-demand when the user navigates to them
const CaseWorkspacePage = lazy(() => import("@/pages/cases/CaseWorkspacePage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const PostCheckout = lazy(() => import("@/pages/PostCheckout"));
const DraftEditorPage = lazy(() => import("@/routes/cases/drafts/DraftEditorPage"));
const FormsLibraryPage = lazy(() => import("@/pages/FormsLibraryPage"));
const FormFillPage = lazy(() => import("@/pages/cases/forms/FormFillPage"));
const FormFillPageV2 = lazy(() => import("@/pages/cases/forms/FormFillPageV2"));
const DocumentEditorPage = lazy(() => import("@/pages/DocumentEditorPage"));
const ImportWizardPage = lazy(() => import("@/routes/cases/imports/ImportWizardPage"));
const CaseMessagesPage = lazy(() => import("@/pages/cases/CaseMessagesPage"));
const GlobalCommunicationsPage = lazy(() => import("@/pages/GlobalCommunicationsPage"));

// Billing V2 pages - lazy loaded
const AccountBillingPage = lazy(() => import("@/pages/settings/AccountBillingPage"));
const NotificationSettingsPage = lazy(() => import("@/pages/settings/NotificationSettingsPage"));
const AccountSettingsPage = lazy(() => import("@/pages/settings/AccountSettingsPage"));
const ProBillingPage = lazy(() => import("@/pages/pro/ProBillingPage"));
const ProConflictsPage = lazy(() => import("@/pages/pro/ProConflictsPage"));
const ProDashboardLandingPage = lazy(() => import("@/pages/pro/ProDashboardLandingPage"));
const ProDashboardClientsTab = lazy(() => import("@/pages/pro/dashboard/ProDashboardClientsTab"));
const MarketplaceRedirect = lazy(() => import("@/pages/marketplace/MarketplaceRedirect"));

// Mobile-to-web session handoff
const MobileHandoffPage = lazy(() => import("@/pages/auth/MobileHandoffPage"));
const ProDashboardListingTab = lazy(() => import("@/pages/pro/dashboard/ProDashboardListingTab"));
const ProDashboardFormsTab = lazy(() => import("@/pages/pro/dashboard/ProDashboardFormsTab"));
const ProDashboardIntegrationsTab = lazy(() => import("@/pages/pro/dashboard/ProDashboardIntegrationsTab"));
const ProDashboardExportsTab = lazy(() => import("@/pages/pro/dashboard/ProDashboardExportsTab"));
const LegacyTabRedirect = lazy(() => import("@/components/pro/LegacyTabRedirect"));

// Phase 3: Dashboard replaced with case resolution (no "all cases view")
// Old: import DashboardPage from "@/pages/DashboardPage";
// New: ResolveCurrentCaseRoute redirects to current case

// Phase 5 Dashboard imports (simplified infrastructure demonstration)
import { CaseDashboardPage } from "@/pages/dashboards/CaseDashboardPage";
// AdminOverviewPage removed — route redirects to /admin (Phase 1.2)

// Connection management
const ConnectionRequestsPage = lazy(() => import("@/pages/ConnectionRequestsPage"));

// Marketplace pages (global directory and pro profiles)
const MarketplaceDirectoryPage = lazy(() => import("@/pages/marketplace/MarketplaceDirectoryPage"));
const ProPublicProfilePage = lazy(() => import("@/pages/marketplace/ProPublicProfilePage"));
const NotificationsPage = lazy(() => import("@/pages/notifications/NotificationsPage"));

// Professional Portal imports - lazy loaded (only for pro users)
const ProLoginPage = lazy(() => import("@/pages/auth/ProLoginPage"));
const ProRegisterPage = lazy(() => import("@/pages/auth/ProRegisterPage"));
const ProDashboardPage = lazy(() => import("@/pages/pro/ProDashboardPage"));
const ProProfileSetupPage = lazy(() => import("@/pages/pro/ProProfileSetupPage"));
const ProPendingApprovalPage = lazy(() => import("@/pages/pro/ProPendingApprovalPage"));
const ProHelpPage = lazy(() => import("@/pages/pro/ProHelpPage"));
const ProReviewRequestsPoolPage = lazy(() => import("@/pages/pro/ProReviewRequestsPoolPage"));
const ProReviewRequestDetailPage = lazy(() => import("@/pages/pro/ProReviewRequestDetailPage"));
const LegalProLandingPage = lazy(() => import("@/pages/LegalProLandingPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const JurisdictionsPage = lazy(() => import("@/pages/admin/JurisdictionsPage"));
const GoalsPage = lazy(() => import("@/pages/admin/GoalsPage"));
const FormDefinitionsPage = lazy(() => import("@/pages/admin/FormDefinitionsPage"));
// AdminHelpPage import removed (dead — file retained, no route assigned) Phase 1.2
const QATestingPage = lazy(() => import("@/pages/admin/QATestingPage"));
const AdminEntitlementsPage = lazy(() => import("@/pages/admin/AdminEntitlementsPage"));
// AdminGuidesPage updated to Phase 4 fragment management UI (WO-ADMIN-001)
const AdminGuidesPage = lazy(() => import("@/pages/admin/AdminGuidesPage"));
const AdminLinksPage = lazy(() => import("@/pages/admin/AdminLinksPage"));
// AdminJurisdictionsPage, AdminJurisdictionZonesPage, AdminFormsPage, AdminGuideErrorsPage
// removed from router — routes eliminated Phase 1.2. Files retained per WO constraint.
// AdminGuideFragmentsPage + AdminFragmentsPage routes redirect to /admin/guides (Phase 4)
const AdminGuideFragmentsPage = lazy(() => import("@/pages/admin/guides/AdminGuideFragmentsPage"));
const AdminFragmentsPage = lazy(() => import("@/pages/admin/AdminFragmentsPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminSupportPage = lazy(() => import("@/pages/admin/AdminSupportPage"));
const AuditLogPage = lazy(() => import("@/pages/admin/AuditLogPage"));
const GuideViewerPage = lazy(() => import("@/pages/admin/GuideViewerPage"));
const GrantSeatsPage = lazy(() => import("@/pages/admin/GrantSeatsPage"));
const InviteAttorneyPage = lazy(() => import("@/pages/cases/InviteAttorneyPage"));
const AcceptInvitationPage = lazy(() => import("@/pages/pro/AcceptInvitationPage"));
const ClioCallbackPage = lazy(() => import("@/pages/pro/ClioCallbackPage"));
const ConnectAccountSetup = lazy(() => import("@/routes/pro/settings/ConnectAccountSetup"));
const ConnectAccountReturn = lazy(() => import("@/routes/pro/settings/ConnectAccountReturn"));
const ConnectAccountRefresh = lazy(() => import("@/routes/pro/settings/ConnectAccountRefresh"));
const CaseGuidesListPage = lazy(() => import("@/pages/cases/CaseGuidesListPage"));
const CaseGuideViewerPage = lazy(() => import("@/pages/cases/CaseGuideViewerPage"));
const AdminConsolePage = lazy(() => import("@/pages/admin/AdminConsolePage"));
const UserAdminPage = lazy(() => import("@/pages/admin/UserAdminPage"));
const UserAdminDetailPage = lazy(() => import("@/pages/admin/UserAdminDetailPage"));
const CaseAdminPage = lazy(() => import("@/pages/admin/CaseAdminPage"));
const CaseAdminDetailPage = lazy(() => import("@/pages/admin/CaseAdminDetailPage"));
const SudoToolsPage = lazy(() => import("@/pages/admin/SudoToolsPage"));
const BulkOperationsPage = lazy(() => import("@/pages/admin/BulkOperationsPage"));
const AiModelUpdatePage = lazy(() => import("@/pages/admin/AiModelUpdatePage"));
// WO-2: Inbound Email Ingestion admin rotation panel
const IntakeAddressAdminPage = lazy(() => import("@/pages/admin/IntakeAddressAdminPage"));
const ProVerificationQueuePage = lazy(() => import("@/pages/admin/ProVerificationQueuePage"));
const ProVerificationDetailPage = lazy(() => import("@/pages/admin/ProVerificationDetailPage"));
const ProOversightPage = lazy(() => import("@/pages/admin/ProOversightPage"));
// JurisdictionConfigPage removed — route redirects to /admin/jurisdictions (WO-ADMIN-001 Phase 2)
const FormsCatalogAdminPage = lazy(() => import("@/pages/admin/FormsCatalogAdminPage"));
const FormsIngestionPage = lazy(() => import("@/pages/admin/FormsIngestionPage"));
const MarketplaceListingsPage = lazy(() => import("@/pages/admin/marketplace/MarketplaceListingsPage"));
const MarketplaceProsPage = lazy(() => import("@/pages/admin/marketplace/MarketplaceProsPage"));
const MarketplaceRequestsPage = lazy(() => import("@/pages/admin/marketplace/MarketplaceRequestsPage"));
const MarketplaceRequestDetailPage = lazy(() => import("@/pages/admin/marketplace/MarketplaceRequestDetailPage"));
const MarketplacePaymentsPage = lazy(() => import("@/pages/admin/marketplace/MarketplacePaymentsPage"));
const MarketplacePoliciesPage = lazy(() => import("@/pages/admin/marketplace/MarketplacePoliciesPage"));
const MarketplaceAnalyticsPage = lazy(() => import("@/pages/admin/marketplace/MarketplaceAnalyticsPage"));

// Readiness Assessment pages - public facing intake flow
const ReadinessPageV2 = lazy(() => import("@/routes/readiness/ReadinessPageV2"));
const ReadinessDownloadPage = lazy(() => import("@/routes/readiness/ReadinessDownloadPage"));
const ReadinessWorkspaceCreatePage = lazy(() => import("@/routes/readiness/ReadinessWorkspaceCreatePage"));
const ReadinessSuccessPage = lazy(() => import("@/routes/readiness/ReadinessSuccessPage"));
const ReadinessCancelPage = lazy(() => import("@/routes/readiness/ReadinessCancelPage"));

// Billing-first subscribe pages (Helios Phase 1)
const SubscribeSuccessPage = lazy(() => import("@/pages/auth/SubscribeSuccessPage"));
const ProSubscribePage = lazy(() => import("@/pages/auth/ProSubscribePage"));
const ProSubscribeSuccessPage = lazy(() => import("@/pages/auth/ProSubscribeSuccessPage"));

// Legal pages
const TermsPage = lazy(() => import("@/pages/legal/Terms"));
const PrivacyPage = lazy(() => import("@/pages/legal/Privacy"));

// 404 Not Found
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Redirect component for workspace tabs
const RedirectToWorkspaceTab = ({ tab }: { tab: string }) => {
  const { caseId } = useParams();
  return <RouterNavigate to={`/cases/${caseId}?tab=${tab}`} replace />;
};

/**
 * CONTRACT ENFORCEMENT: buildPhaseRoutes() now returns PLAIN components.
 * CaseLayout wrapper is applied ONCE at parent route level (see line ~850).
 * 
 * BEFORE (VIOLATION): Each phase wrapped in <CaseLayout> = 15 instances
 * AFTER (COMPLIANT): Returns plain component, parent provides layout = 1 instance
 */
function buildPhaseRoutes() {
  return PHASES.map((phase) => {
    const phasePath = phase.path || undefined; // undefined = index route
    return {
      path: phasePath,
      index: !phasePath, // If no path, make it the index route
      lazy: async () => {
        try {
          console.log(`[buildPhaseRoutes] Loading phase: ${phase.key} from ${phase.path}`);
          const mod = await phase.loader();
          console.log(`[buildPhaseRoutes] Module loaded for ${phase.key}:`, Object.keys(mod));
          const exportKey = phase.exportName ?? "default";
          const Component = mod[exportKey] as ComponentType<unknown> | undefined;

          if (!Component) {
            console.error(`[buildPhaseRoutes] Phase ${phase.key} is missing export "${exportKey}". Available exports:`, Object.keys(mod));
            throw new Error(`Phase ${phase.key} is missing export \"${exportKey}\".`);
          }

          const PhaseComponent = Component as ComponentType<Record<string, unknown>>;
          console.log(`[buildPhaseRoutes] Successfully loaded component for ${phase.key}`);

          // ✅ COMPLIANT: Return PLAIN component (no CaseLayout wrapper)
          return {
            element: <PhaseComponent />,
            errorElement: <RouteError />,
          };
        } catch (error) {
          console.error(`[buildPhaseRoutes] FATAL ERROR loading phase ${phase.key}:`, error);
          // If the error is a SyntaxError with '<', the server returned HTML
          // instead of JS — this means the chunk was replaced by a new deploy.
          // Force a full reload so the browser fetches the fresh index.html + chunks.
          if (
            error instanceof SyntaxError &&
            (error.message.includes('<') || error.message.includes('Unexpected token'))
          ) {
            console.warn('[buildPhaseRoutes] Stale chunk detected via SyntaxError, reloading page...');
            window.location.reload();
          }
          throw error;
        }
      },
    };
  });
}

const phaseRoutes = buildPhaseRoutes();

console.log('[Router] Phase routes configured:', phaseRoutes.map(r => ({ path: r.path, index: r.index })));
console.log('[Router] 🔍 FORMS ROUTE CHECK:', phaseRoutes.find(r => r.path === 'forms'));
console.log('[Router] 🔍 TOTAL PHASE ROUTES:', phaseRoutes.length);

// 🔍 PHASE 2 DIAGNOSTIC: Error boundary that logs the actual error
function FormsLibraryErrorBoundary() {
  const error = useRouteError() as any;
  
  // Log error to console AND window for Cypress access 
  console.error('[FormsLibraryErrorBoundary] Error caught:', error);
  if (typeof window !== 'undefined') {
    (window as any).__FORMS_ERROR__ = {
      message: error?.message || 'Unknown error',
      stack: error?.stack || null,
      fullError: error
    };
  }
  
  return (
    <div data-testid="forms.error.boundary" className="flex h-screen items-center justify-center text-slate-50">
      <div className="max-w-2xl">
        <h1 className="text-xl font-bold text-red-400">Forms Library Error</h1>
        <p className="mt-2">An error occurred loading the forms library.</p>
        {error && (
          <pre className="mt-4 rounded bg-slate-900 p-4 text-xs text-slate-300 overflow-auto max-h-96">
            {error.message || 'No message'}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        )}
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  // Onboarding routes - Setup wizard (WO-SETUP-WIZARD-001)
  {
    path: "/onboarding/setup",
    element: (
      <RequireAuth>
        <SetupWizardPage />
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/onboarding/basic",
    element: (
      <RequireAuth>
        <OnboardingBasicPage />
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/onboarding/confirm",
    element: (
      <RequireAuth>
        <OnboardingConfirmPage />
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />
  },
  
  // Public auth routes
  { path: "/login", element: <LoginPage />, errorElement: <ErrorBoundary /> },
  
  // P0 WARNING: App /signup is SRL claim-based account creation.
  // Marketing site (threadlock.ai/signup) handles SRL/Pro role selection and links to app subscribe flows.
  // DO NOT add role selection UI here - it duplicates marketing and breaks claim-based flow.
  // If user lands here without claim, guide them to /subscribe (billing-first architecture).
  { path: "/signup", element: <SignupPage />, errorElement: <ErrorBoundary /> },
  
  { path: "/reset-password", element: <ResetPasswordPage />, errorElement: <ErrorBoundary /> },
  { path: "/auth/action", element: <ActionCodeRoute />, errorElement: <ErrorBoundary /> },
  { path: "/auth/mobile-handoff", element: <Suspense fallback={<LoadingSpinner />}><MobileHandoffPage /></Suspense>, errorElement: <ErrorBoundary /> },
  { path: "/verify-email", element: <EmailVerificationInterstitialPage />, errorElement: <ErrorBoundary /> },
  
  // SRL Subscribe flow (billing-first)
  { path: "/subscribe", element: <SubscribePage />, errorElement: <ErrorBoundary /> },
  { 
    path: "/subscribe/success", 
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <SubscribeSuccessPage />
      </Suspense>
    ), 
    errorElement: <ErrorBoundary /> 
  },
  
  // Pro Subscribe flow (billing-first)
  { 
    path: "/pro/subscribe", 
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <ProSubscribePage />
      </Suspense>
    ), 
    errorElement: <ErrorBoundary /> 
  },
  { 
    path: "/pro/subscribe/success", 
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <ProSubscribeSuccessPage />
      </Suspense>
    ), 
    errorElement: <ErrorBoundary /> 
  },
  
  // Legal pages (public)
  { 
    path: "/terms", 
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <TermsPage />
      </Suspense>
    ), 
    errorElement: <ErrorBoundary /> 
  },
  { 
    path: "/privacy", 
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <PrivacyPage />
      </Suspense>
    ), 
    errorElement: <ErrorBoundary /> 
  },
  
  // Marketplace Redirect - Navigate users to authenticated marketplace
  // Public marketplace removed to keep users in authenticated context
  {
    path: "/marketplace",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <MarketplaceRedirect />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  
  // Readiness Assessment - Public intake flow (no AppShell)
  {
    path: "/readiness",
    element: <PublicStandaloneLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReadinessPageV2 />
          </Suspense>
        ),
      },
      {
        path: "download",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReadinessDownloadPage />
          </Suspense>
        ),
      },
      {
        path: "workspace-create",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReadinessWorkspaceCreatePage />
          </Suspense>
        ),
      },
      {
        path: "success",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReadinessSuccessPage />
          </Suspense>
        ),
      },
      {
        path: "cancel",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReadinessCancelPage />
          </Suspense>
        ),
      },
    ],
  },
  
  // DEV-ONLY: Debug page for P0 routing contract verification
  ...(import.meta.env.DEV ? [{
    path: "/__debug/case-routing",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <CaseRoutingDebugPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />
  }] : []),
  
  // Professional Portal routes (no auth required for login/register/landing)
  { path: "/legal-pro", element: <Suspense fallback={<LoadingSpinner />}><LegalProLandingPage /></Suspense>, errorElement: <ErrorBoundary /> },
  { path: "/pro/login", element: <Suspense fallback={<LoadingSpinner />}><ProLoginPage /></Suspense>, errorElement: <ErrorBoundary /> },
  
  // P0 WARNING: App /pro/register is Pro claim-based account creation.
  // Marketing site (threadlock.ai/signup) handles SRL/Pro role selection and links to /pro/subscribe.
  // DO NOT add role selection UI here - it duplicates marketing and breaks claim-based flow.
  // If user lands here without claim, guide them to /pro/subscribe (billing-first architecture).
  { path: "/pro/register", element: <Suspense fallback={<LoadingSpinner />}><ProRegisterPage /></Suspense>, errorElement: <ErrorBoundary /> },
  { path: "/pro/accept-invitation/:token", element: <Suspense fallback={<LoadingSpinner />}><AcceptInvitationPage /></Suspense>, errorElement: <ErrorBoundary /> },
  
  // Professional Portal - Unified Layout
  {
    path: "/pro",
    element: (
      <RequireAuth>
        <RequireProAccess>
          <ProPortalLayout />
        </RequireProAccess>
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      // Index: redirect bare /pro to /pro/dashboard
      {
        index: true,
        element: <Navigate to="/pro/dashboard" replace />,
      },
      {
        path: "dashboard",
        element: (
          <RequireActiveSubscription redirectTo="/pro/billing">
            <Suspense fallback={<LoadingSpinner />}>
              <ProDashboardPage />
            </Suspense>
          </RequireActiveSubscription>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ProDashboardLandingPage />
              </Suspense>
            )
          },
          {
            path: "clients",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ProDashboardClientsTab />
              </Suspense>
            )
          },
          {
            path: "listing",
            element: (
              <RequireVerification>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProDashboardListingTab />
                </Suspense>
              </RequireVerification>
            )
          },
          {
            path: "review-queue",
            element: <Navigate to="/pro/requests" replace />
          },
          {
            path: "open-requests",
            element: <Navigate to="/pro/requests" replace />
          },
          {
            path: "forms",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ProDashboardFormsTab />
              </Suspense>
            )
          },
          {
            path: "integrations",
            element: (
              <RequireVerification>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProDashboardIntegrationsTab />
                </Suspense>
              </RequireVerification>
            )
          },
          {
            path: "exports",
            element: (
              <RequireVerification>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProDashboardExportsTab />
                </Suspense>
              </RequireVerification>
            )
          }
        ]
      },
      {
        path: "billing",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProBillingPage />
          </Suspense>
        )
      },
      {
        path: "setup",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProProfileSetupPage />
          </Suspense>
        )
      },
      {
        path: "pending-approval",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProPendingApprovalPage />
          </Suspense>
        )
      },
      {
        path: "conflicts",
        element: (
          <RequireActiveSubscription redirectTo="/pro/billing">
            <RequireVerification>
              <Suspense fallback={<LoadingSpinner />}>
                <ProConflictsPage />
              </Suspense>
            </RequireVerification>
          </RequireActiveSubscription>
        )
      },
      {
        path: "requests",
        element: (
          <RequireActiveSubscription redirectTo="/pro/billing">
            <RequireVerification>
              <Suspense fallback={<LoadingSpinner />}>
                <ProReviewRequestsPoolPage />
              </Suspense>
            </RequireVerification>
          </RequireActiveSubscription>
        )
      },
      {
        path: "requests/:requestId",
        element: (
          <RequireActiveSubscription redirectTo="/pro/billing">
            <RequireVerification>
              <Suspense fallback={<LoadingSpinner />}>
                <ProReviewRequestDetailPage />
              </Suspense>
            </RequireVerification>
          </RequireActiveSubscription>
        )
      },
      {
        path: "integrations/clio/callback",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ClioCallbackPage />
          </Suspense>
        )
      },
      {
        path: "help",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProHelpPage />
          </Suspense>
        )
      },
      {
        path: "settings/connect",
        element: (
          <RequireActiveSubscription redirectTo="/pro/billing">
            <Suspense fallback={<LoadingSpinner />}>
              <ConnectAccountSetup />
            </Suspense>
          </RequireActiveSubscription>
        )
      },
      {
        path: "settings/connect/return",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ConnectAccountReturn />
          </Suspense>
        )
      },
      {
        path: "settings/connect/refresh",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ConnectAccountRefresh />
          </Suspense>
        )
      }
    ]
  },
  
  // Admin routes (protected with admin guard, using AdminLayout)
  { 
    path: "/admin",
    element: (
      <RequireAuth>
        <RequireAdminAccess>
          <AdminLayout />
        </RequireAdminAccess>
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      // Admin Dashboard (index route)
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminDashboardPage />
          </Suspense>
        )
      },
      // /admin/overview → /admin (merged per Phase 1.2 audit)
      { path: "overview", element: <Navigate to="/admin" replace /> },
      // Core Admin Routes
      {
        path: "help",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <HelpCenterResearchPage />
          </Suspense>
        )
      },
      {
        path: "support",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminSupportPage />
          </Suspense>
        )
      },
      // /admin/audit renamed to /admin/audit-log (Phase 1.2); old path redirects
      { path: "audit", element: <Navigate to="/admin/audit-log" replace /> },
      {
        path: "audit-log",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AuditLogPage />
          </Suspense>
        )
      },
      // User Management
      {
        path: "users",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <UserAdminPage />
          </Suspense>
        )
      },
      {
        path: "users/:userId",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <UserAdminDetailPage />
          </Suspense>
        )
      },
      // Case Management
      {
        path: "cases",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CaseAdminPage />
          </Suspense>
        )
      },
      {
        path: "cases/:caseId",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CaseAdminDetailPage />
          </Suspense>
        )
      },
      // Professionals
      {
        path: "pro-verification-queue",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProVerificationQueuePage />
          </Suspense>
        )
      },
      {
        path: "pro-verification/:userId",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProVerificationDetailPage />
          </Suspense>
        )
      },
      {
        path: "pro-oversight",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProOversightPage />
          </Suspense>
        )
      },
      // Marketplace
      {
        path: "marketplace/listings",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketplaceListingsPage />
          </Suspense>
        )
      },
      {
        path: "marketplace/pros",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketplaceProsPage />
          </Suspense>
        )
      },
      {
        path: "marketplace/requests",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketplaceRequestsPage />
          </Suspense>
        )
      },
      {
        path: "marketplace/requests/:requestId",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketplaceRequestDetailPage />
          </Suspense>
        )
      },
      {
        path: "marketplace/payments",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketplacePaymentsPage />
          </Suspense>
        )
      },
      {
        path: "marketplace/analytics",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketplaceAnalyticsPage />
          </Suspense>
        )
      },
      {
        path: "marketplace/policies",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketplacePoliciesPage />
          </Suspense>
        )
      },
      // Configuration
      {
        path: "jurisdictions",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <JurisdictionsPage />
          </Suspense>
        )
      },
      {
        // Redirected to /admin/jurisdictions per WO-ADMIN-001 Phase 2
        path: "config/jurisdictions",
        element: <Navigate to="/admin/jurisdictions" replace />
      },
      {
        path: "goals",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <GoalsPage />
          </Suspense>
        )
      },
      // Forms
      {
        path: "forms",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <FormDefinitionsPage />
          </Suspense>
        )
      },
      {
        path: "config/forms",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <FormsCatalogAdminPage />
          </Suspense>
        )
      },
      // Guides
      {
        path: "guides",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminGuidesPage />
          </Suspense>
        )
      },
      // guides/jurisdictions, guides/zones, guides/forms, guides/errors removed Phase 1.2
      // (jurisdiction management consolidates to /admin/jurisdictions in Phase 2;
      //  guide forms management at /admin/forms; errors rolled into /admin/guides)
      // guides/fragments redirects to /admin/guides (Phase 4 — fragment mgmt consolidated)
      {
        path: "guides/fragments",
        element: <Navigate to="/admin/guides" replace />
      },
      {
        path: "links",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminLinksPage />
          </Suspense>
        )
      },
      // guides/errors removed Phase 1.2 — rolled into /admin/guides
      {
        path: "guides/:guideId",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <GuideViewerPage />
          </Suspense>
        )
      },
      // Operations
      // fragments redirects to /admin/guides (Phase 4 — fragment mgmt consolidated)
      {
        path: "fragments",
        element: <Navigate to="/admin/guides" replace />
      },
      // Super Admin Only Routes
      {
        path: "qa",
        element: (
          <RequireSuperAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <QATestingPage />
            </Suspense>
          </RequireSuperAdmin>
        )
      },
      {
        path: "entitlements",
        element: (
          <RequireSuperAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <AdminEntitlementsPage />
            </Suspense>
          </RequireSuperAdmin>
        )
      },
      {
        path: "settings",
        element: (
          <RequireSuperAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <AdminSettingsPage />
            </Suspense>
          </RequireSuperAdmin>
        )
      },
      {
        path: "grant-seats",
        element: (
          <RequireSuperAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <GrantSeatsPage />
            </Suspense>
          </RequireSuperAdmin>
        )
      },
      {
        path: "forms-ingestion",
        element: (
          <RequireSuperAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <FormsIngestionPage />
            </Suspense>
          </RequireSuperAdmin>
        )
      },
      {
        path: "sudo",
        element: (
          <RequireSuperAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <SudoToolsPage />
            </Suspense>
          </RequireSuperAdmin>
        )
      },
      {
        path: "bulk-operations",
        element: (
          <RequireSuperAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <BulkOperationsPage />
            </Suspense>
          </RequireSuperAdmin>
        )
      },
      {
        path: "ai-model-update",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AiModelUpdatePage />
          </Suspense>
        )
      },
      // WO-2: Email Intake address rotation panel
      {
        path: "intake-addresses",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <IntakeAddressAdminPage />
          </Suspense>
        )
      },
      {
        path: "intake-addresses/:caseId",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <IntakeAddressAdminPage />
          </Suspense>
        )
      },
    ]
  },
  // BYOA Invitation Flow
  {
    path: "/cases/:caseId/invite-attorney",
    element: (
      <RequireAuth>
        <Suspense fallback={<LoadingSpinner />}>
          <InviteAttorneyPage />
        </Suspense>
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />
  },
  
  // Public share viewer - no authentication required
  { path: "/share/:linkId", element: <ShareViewerPage />, errorElement: <ErrorBoundary /> },
  
  // Public invite accept/decline - authentication required
  { path: "/invites/:inviteId", element: <InviteAcceptPage />, errorElement: <ErrorBoundary /> },
  {
    element: <AppShell />,
    children: [
      {
        element: <RequireAuth />,
        children: [
          // Routes with AppLayout (unified navigation)
          {
            element: <AppLayout />,
            children: [
              {
                element: <RequireSubscription />,
                children: [
                  // Onboarding route (subscription required, NO RequireSrlHasCase)
                  {
                    path: "onboarding/create-case",
                    element: <CreateCasePage />,
                    errorElement: <RouteError />
                  },
                  { index: true, element: <Navigate to="/dashboard" replace /> },

                  // Routes that REQUIRE a case (wrapped with RequireSrlHasCase)
                  {
                    element: <RequireSrlHasCase />,
                    children: [
                      {
                        path: "dashboard",
                        element: <ResolveCurrentCaseRoute />,
                        errorElement: <RouteError />
                      },
                  
                      /**
                       * ========================================================================
                       * ROUTING CONTRACT ENFORCEMENT - SINGLE CASELAYOUT AUTHORITY
                       * ========================================================================
                       *
                       * ✅ RULE: CaseLayout appears EXACTLY ONCE in the entire router tree.
                       * ✅ LOCATION: Parent route /cases/:caseId (below)
                       * ✅ ALL case-scoped routes MUST be children of this parent
                       *
                       * PARAM CONTRACT:
                       * - Use :caseId (NOT :id) for case parameter
                       * - Use :draftId, :filingId, :evidenceId for entities
                       * - NO destructuring aliases: const { caseId } = useParams() ✅
                       * - FORBIDDEN: const { id: caseId } = useParams() ❌
                       *
                       * Layout mounting verified by:
                       * - Runtime assertion in CaseLayout (checks header count = 1)
                       * - Cypress test: cypress/e2e/layout-invariant.cy.ts
                       * - Debug page: /__debug/case-routing (DEV only)
                       *
                       * VIOLATION = IMMEDIATE BUILD FAILURE + CONSOLE ERROR
                       * 
                       * BUG FIX 2026-02-01: Removed duplicate RequireAuth wrapper
                       * (route already inside parent RequireAuth at line 1166)
                       * ========================================================================
                       */
                      {
                        path: "cases/:caseId",
                        element: (
                          <RequireCaseAccess>
                            <CaseLayout />
                          </RequireCaseAccess>
                        ),
                        errorElement: <RouteError />,
                        children: [
                      // Index route: Main Case Workspace
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <CaseWorkspacePage />
                          </Suspense>
                        ),
                      },
                      
                      // Guide routes (user-facing)
                      {
                        path: "guides",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <CaseGuidesListPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "guides/:guideId",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <CaseGuideViewerPage />
                          </Suspense>
                        ),
                      },

                      // Import Wizard (bulk evidence ingestion)
                      {
                        path: "imports",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <ImportWizardPage />
                          </Suspense>
                        ),
                      },

                      // Messages & Communication Center
                      {
                        path: "messages",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <CaseMessagesPage />
                          </Suspense>
                        ),
                      },
                      
                      // T-75: Activity page - unified dual-collection feed
                      {
                        path: "activity",
                        lazy: async () => {
                          const { default: ActivityPage } = await import("@/routes/cases/activity/ActivityPage");
                          return {
                            element: <ActivityPage />,
                          };
                        },
                      },
                      
                      // Legacy reports route kept for backwards compat
                      {
                        path: "reports/activity-log",
                        lazy: async () => {
                          const { default: ActivityLogPage } = await import("@/routes/cases/reports/ActivityLogPage");
                          return {
                            element: <ActivityLogPage />,
                          };
                        },
                      },
                      
                      {
                        path: "journal/search",
                        lazy: async () => {
                          const { default: JournalSearchPage } = await import("@/routes/cases/journal/JournalSearchPage");
                          return {
                            element: <JournalSearchPage />,
                          };
                        },
                      },
                      {
                        path: "journal/:entryId",
                        lazy: async () => {
                          const { default: JournalEntryDetailPageV2 } = await import("@/routes/cases/journal/JournalEntryDetailPageV2");
                          return {
                            element: <JournalEntryDetailPageV2 />,
                          };
                        },
                      },
                      {
                        path: "forms/:formInstanceId/fill",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <FormFillPageV2 />
                          </Suspense>
                        ),
                      },
                      {
                        path: "marketplace",
                        lazy: async () => {
                          const { default: MarketplacePage } = await import("@/pages/marketplace/MarketplacePage");
                          return {
                            element: <MarketplacePage />,
                          };
                        },
                      },
                      {
                        path: "my-review-requests",
                        lazy: async () => {
                          const { default: MyReviewRequestsPage } = await import("@/pages/marketplace/MyReviewRequestsPage");
                          return {
                            element: <MyReviewRequestsPage />,
                          };
                        },
                      },
                      {
                        path: "review-requests/new",
                        lazy: async () => {
                          const { default: ReviewRequestChooserPage } = await import("@/pages/marketplace/ReviewRequestChooserPage");
                          return {
                            element: <ReviewRequestChooserPage />,
                          };
                        },
                      },
                      {
                        path: "review-requests/new/broadcast",
                        lazy: async () => {
                          const { default: BroadcastRequestFormPage } = await import("@/pages/marketplace/BroadcastRequestFormPage");
                          return {
                            element: <BroadcastRequestFormPage />,
                          };
                        },
                      },
                      {
                        path: "review-requests/:requestId",
                        lazy: async () => {
                          const { default: ReviewRequestStatusPage } = await import("@/pages/marketplace/ReviewRequestStatusPage");
                          return {
                            element: <ReviewRequestStatusPage />,
                          };
                        },
                      },
                      {
                        path: "request-review",
                        lazy: async () => {
                          const { default: RequestReviewPage } = await import("@/pages/RequestReviewPage");
                          return {
                            element: <RequestReviewPage />,
                          };
                        },
                      },
                      {
                        path: "marketplace/:professionalId/confirm",
                        lazy: async () => {
                          const { default: MarketplaceConfirmPage } = await import("@/pages/marketplace/MarketplaceConfirmPage");
                          return {
                            element: <MarketplaceConfirmPage />,
                          };
                        },
                      },
                      {
                        path: "marketplace/success",
                        lazy: async () => {
                          const { default: MarketplaceSuccessPage } = await import("@/pages/marketplace/MarketplaceSuccessPage");
                          return {
                            element: <MarketplaceSuccessPage />,
                          };
                        },
                      },
                      {
                        path: "filings/:filingId",
                        lazy: async () => {
                          const { default: CaseFilingDetailPage } = await import("@/pages/cases/CaseFilingDetailPage");
                          return {
                            element: <CaseFilingDetailPage />,
                          };
                        },
                      },
                      {
                        path: "filings/:filingId/edit",
                        lazy: async () => {
                          const { default: FilingBuilderPage } = await import("@/pages/cases/filings/FilingBuilderPage");
                          return {
                            element: <FilingBuilderPage />,
                          };
                        },
                      },
                      {
                        path: "filings/:filingId/preview",
                        lazy: async () => {
                          const { default: FilingPreviewPage } = await import("@/pages/cases/filings/FilingPreviewPage");
                          return {
                            element: <FilingPreviewPage />,
                          };
                        },
                      },
                      { path: "forms/:formId", element: <RedirectToWorkspaceTab tab="forms" /> },
                      // P2-1 WO5: exhibits/bates unshipped — replaced redirect with actual BatesPage
                      {
                        path: "exhibits/bates",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <BatesPage />
                          </Suspense>
                        ),
                      },
                      { path: "exhibits/tags", element: <RedirectToWorkspaceTab tab="evidence" /> },
                      // New draft creation
                      {
                        path: "drafts/new",
                        lazy: async () => {
                          const { default: NewDraftPage } = await import("@/routes/cases/drafts/NewDraftPage");
                          return {
                            element: <NewDraftPage />,
                          };
                        },
                      },
                      // Draft editor (nested route for editing individual drafts)
                      {
                        path: "drafts/:draftId",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <DraftEditorPage />
                          </Suspense>
                        ),
                      },
                      // AI Drafts page (P2 governance implementation)
                      {
                        path: "drafts/ai",
                        lazy: async () => {
                          const { default: AiDraftsPage } = await import("@/pages/cases/AiDraftsPage");
                          return {
                            element: <AiDraftsPage />,
                          };
                        },
                      },
                      
                      // P0 LEGACY ROUTE REDIRECT: /cases/:caseId/editor/:documentId → /documents/:documentId
                      // Prevents stale bookmarks and deep links from breaking
                      // DO NOT REMOVE - users may have bookmarked legacy URLs
                      {
                        path: "editor/:documentId",
                        element: (
                          <Suspense fallback={<LoadingSpinner />}>
                            <LegacyEditorRedirect />
                          </Suspense>
                        ),
                      },
                      
                      // TEST: Manual inline filings route
                      {
                        path: "filings",
                        lazy: async () => {
                          console.log('[Router] Loading filings manually');
                          const mod = await import("@/pages/cases/CaseFilingsPage");
                          console.log('[Router] Filings module loaded:', Object.keys(mod));
                          return {
                            element: <mod.default />,
                          };
                        },
                      },
                      
                      // Phase routes (dynamically generated from PHASES array)
                      // These are NOW children of /cases/:caseId parent (CaseLayout applied once above)
                      // Redirect legacy /build route → case index (Overview renamed to Build Case)
                      {
                        path: "build",
                        element: <Navigate to=".." replace />,
                      },
                      // P0-1 WO5: Redirect /documents → /drafts (canonical merge)
                      {
                        path: "documents",
                        element: <Navigate to="../drafts" replace />,
                      },
                      ...phaseRoutes,
                    ],
                  },
                    ],
                  },

                  // Routes that DON'T require a case (NOT wrapped with RequireSrlHasCase)
                  { path: "cases", element: <CasesPage />, errorElement: <RouteError /> },
                  { path: "cases/new", element: <NewCasePage />, errorElement: <RouteError /> },
                  { path: "search", element: <SearchPage /> },
                  
                  // Help system routes (Phase 1: Zero 404, Zero Chaos)
                  // Legacy route: /help → Help Center Research Page (AI assistant)
                  { path: "help", element: <HelpCenterResearchPage /> },
                  // New structured help content routes (from helpRegistry.ts)
                  { 
                    path: "help/:topic", 
                    lazy: async () => {
                      const { default: HelpPage } = await import("@/pages/help/HelpPage");
                      return { element: <HelpPage /> };
                    },
                    errorElement: <RouteError />
                  },
                  
                  { path: "settings", element: <SettingsPage /> },
                  {
                    path: "settings/account",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <AccountSettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "settings/billing",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <AccountBillingPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "settings/notifications",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <NotificationSettingsPage />
                      </Suspense>
                    ),
                  },
                  // Connection requests (SRL & Pro)
                  {
                    path: "connections",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <ConnectionRequestsPage />
                      </Suspense>
                    ),
                  },
                  // WO7-P1: Marketplace directory (all pros, no case context required)
                  // MUST be registered before /marketplace/:proId to prevent slug collision
                  {
                    path: "marketplace/directory",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <MarketplaceDirectoryPage />
                      </Suspense>
                    ),
                  },
                  // WO7-P1: Pro public profile page (registered after /marketplace/directory)
                  {
                    path: "marketplace/:proId",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <ProPublicProfilePage />
                      </Suspense>
                    ),
                  },
                  // WO7-P2: Global notification inbox
                  {
                    path: "notifications",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <NotificationsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "forms",
                    element: (
                      <Suspense fallback={<div data-testid="forms.suspense.loading" className="flex h-screen items-center justify-center"><LoadingSpinner /></div>}>
                        <FormsLibraryPage />
                      </Suspense>
                    ),
                    errorElement: <FormsLibraryErrorBoundary />
                  },
                  {
                    path: "documents/:documentId",
                    element: (
                      <Suspense fallback={<LoadingSpinner />}>
                        <DocumentEditorPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "legal-disclaimer",
                    lazy: async () => {
                      const { default: LegalDisclaimerPage } = await import("@/pages/LegalDisclaimerPage");
                      return { element: <LegalDisclaimerPage /> };
                    },
                    errorElement: <RouteError />,
                  },
                ],
              },
              // Root index redirect
              { index: true, element: <Navigate to="/dashboard" replace /> },
              // Routes without subscription but with AppLayout
              // CRITICAL: /billing must be reachable for unsubscribed users (allowlist)
              // Wrapped in Suspense to prevent lazy import failures
              { 
                path: "billing", 
                element: (
                  <Suspense fallback={<LoadingSpinner />}>
                    <BillingPage />
                  </Suspense>
                ) 
              },
              { path: "profile", element: <ProfilePage /> },
            ],
          },
          // Routes without AppLayout
          { path: "post-checkout", element: <PostCheckout /> },
          // REMOVED: Standalone onboarding route - now integrated into CaseWorkspacePage/DashboardTab
          // Old route: { path: "/wizard/onboarding", element: <OnboardingPage /> }
          // Onboarding wizard now appears automatically in DashboardTab when onboardingCompleted === false
        ],
      },
    ]
  },
  
  // Legacy/stale Stripe portal return URL redirect (B.1.4)
  // Stripe dashboard was previously configured with /account/billing as the return URL.
  // This catches users returning from the Stripe portal and redirects them to the correct route.
  {
    path: "account/billing",
    element: <Navigate to="/settings/billing" replace />,
  },

  // Catch-all 404 - branded NotFound page (Helios Phase 1)
  {
    path: "*",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <NotFoundPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />
  }
]);